import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth, getRoles, canAccess, isPlatformOwner, isSuperAdmin } from "@/context/AuthContext";
import { api } from "@/lib/api";
import BrandLogo from "@/components/common/BrandLogo";

/** In-flight access-status cache — fetched once per session. */
let _accessCache = null;

async function fetchAccessStatus() {
  if (_accessCache) return _accessCache;
  try {
    const r = await api.get("/access/status");
    _accessCache = r.data || { status: "unknown" };
  } catch (e) {
    // If backend returns 403 with INVITE_REQUIRED we still capture it here.
    const d = e?.response?.data;
    _accessCache = {
      status: d?.status || d?.detail || "invite_required",
      message: d?.message || d?.detail || "",
      blocked: true,
    };
  }
  return _accessCache;
}

const BLOCKED_STATES = new Set(["pending", "rejected", "revoked", "suspended", "blocked", "invite_required", "disabled"]);

/** Reset the cache — call from logout. */
export function resetAccessCache() { _accessCache = null; }

export default function ProtectedRoute({ children, portal = "client" }) {
  const { user, platform, b2b, initializing, isAuthenticated } = useAuth();
  const loc = useLocation();
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessOk, setAccessOk] = useState(false);

  const roles = getRoles(user, platform, b2b);
  const isOwner = isPlatformOwner(roles) || isSuperAdmin(roles);

  // Fetch access status once per session (bypass for platform owners / super admins).
  useEffect(() => {
    let alive = true;
    if (!user) return;
    if (isOwner) { setAccessOk(true); setAccessChecked(true); return; }
    (async () => {
      const s = await fetchAccessStatus();
      if (!alive) return;
      // Consumer registration is open: only explicitly blocked states are gated.
      const st = String(s?.access_status || s?.status || "").toLowerCase();
      const blocked = BLOCKED_STATES.has(st) || (s?.blocked === true && st !== "unknown");
      setAccessOk(!blocked);
      setAccessChecked(true);
    })();
    return () => { alive = false; };
  }, [user, isOwner]);

  if (initializing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream-50 gap-4" data-testid="auth-loading">
        <BrandLogo size="2xl" className="animate-pulse" />
        <div className="text-ink-500 font-heading text-lg tracking-tight">Inner Compass</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  }

  // Wait for the access status result before routing.
  if (!accessChecked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream-50 gap-4" data-testid="access-checking">
        <BrandLogo size="2xl" className="animate-pulse" />
      </div>
    );
  }

  if (!accessOk) {
    // Prevent the user from bouncing between /access-required and every protected page.
    if (loc.pathname !== "/access-required") {
      return <Navigate to="/access-required" replace />;
    }
  }

  if (!canAccess(roles, portal)) {
    // Authenticated + accessOk but wrong portal → send them home to /app.
    return <Navigate to="/app" replace />;
  }

  return children;
}
